<template>
  <div>
    <div class="text-lg font-extrabold tracking-tight">Налаштування · Профіль</div>
    <div class="mt-1 text-sm text-white/65">
      Редагування ніку, email, опису та аватарки. Збереження асинхронне.
    </div>

    <div class="mt-6 grid grid-cols-1 gap-4">
      <div class="acc-glass rounded-2xl p-5 border border-white/10">
        <div class="text-sm font-extrabold">Основні дані</div>
        <form class="mt-4 space-y-3" @submit.prevent="saveProfile">
          <div>
            <label for="settings-profile-name" class="text-xs text-white/55 font-semibold mb-1 block">
              Ім'я у профілі
              <span class="text-red-300" aria-hidden="true">*</span>
              <span class="sr-only">(обов'язкове поле)</span>
            </label>
            <input
              id="settings-profile-name"
              v-model="form.name"
              class="foxy-input text-sm"
              placeholder="Ваше ім'я"
              aria-required="true"
              :aria-invalid="nameSaveError ? 'true' : 'false'"
              :aria-describedby="nameSaveError ? 'settings-profile-name-error' : undefined"
              :disabled="!isOwner || saving"
              @input="nameSaveError = ''"
            />
            <div
              v-if="nameSaveError"
              id="settings-profile-name-error"
              class="mt-1 text-xs text-red-700 dark:text-red-300"
              aria-live="polite"
            >
              {{ nameSaveError }}
            </div>
          </div>
          <div>
            <label for="settings-username" class="text-xs text-white/55 font-semibold mb-1 block">Юзернейм</label>
            <div class="relative">
              <span class="absolute inset-y-0 left-3 flex items-center text-sm text-white/45 pointer-events-none" aria-hidden="true">@</span>
              <input
                id="settings-username"
                v-model="form.username"
                class="foxy-input text-sm settings-username-input"
                placeholder="Введіть юзернейм"
                maxlength="30"
                autocomplete="username"
                spellcheck="false"
                :disabled="!isOwner || saving"
                @input="onUsernameInput"
              />
            </div>
            <div v-if="showUsernameHelper" class="mt-1 text-xs text-white/45">{{ usernameHelperMessage }}</div>
            <div v-if="usernameSaveError" class="mt-1 text-xs text-red-700 dark:text-red-300" aria-live="polite">
              {{ usernameSaveError }}
            </div>
            <div
              v-else-if="usernameAvailabilityMessage"
              class="mt-1 text-xs"
              :class="usernameAvailabilityClass"
              aria-live="polite"
            >
              {{ usernameAvailabilityMessage }}
            </div>
          </div>
          <div>
            <div class="text-xs text-white/55 font-semibold mb-1">Email</div>
            <input
              v-model="form.email"
              type="email"
              class="foxy-input text-sm"
              placeholder="mail@example.com"
              :disabled="!isOwner || saving"
            />
          </div>
          <div>
            <div class="text-xs text-white/55 font-semibold mb-1">Про себе</div>
            <textarea
              v-model="form.bio"
              class="foxy-textarea text-sm"
              rows="4"
              maxlength="1000"
              placeholder="Про себе..."
              :disabled="!isOwner || saving"
            />
            <div class="mt-1 text-right text-xs text-white/45">{{ bioLength }}/1000</div>
          </div>
          <div v-if="message" class="text-xs text-emerald-300">{{ message }}</div>
          <div v-if="error" class="text-xs text-red-300">{{ error }}</div>
          <button type="submit" class="acc-btn-primary w-full justify-center" :disabled="!isOwner || saving">
            {{ saving ? 'Збереження...' : 'Зберегти' }}
          </button>
        </form>
      </div>

      <div class="acc-glass rounded-2xl p-5 border border-white/10">
        <div class="text-sm font-extrabold">Аватар</div>
        <div class="mt-4 flex items-center gap-4">
          <user-avatar
            :src="profile && profile.avatar ? profile.avatar : ''"
            :name="profile && profile.name ? profile.name : form.username"
            alt="Аватар користувача"
            size="xl"
            class="border border-white/10 bg-white/10"
          />
          <div class="flex-1">
            <div class="text-xs text-white/60">PNG, JPG/JPEG, WEBP · максимум 2 МБ.</div>
            <div class="mt-2 flex items-center gap-2">
              <label class="acc-btn" :class="{ 'opacity-60 pointer-events-none': !isOwner || avatarSaving }">
                {{ avatarSaving ? 'Завантаження...' : 'Змінити' }}
                <input type="file" class="hidden" accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp" :disabled="!isOwner || avatarSaving" @change="onAvatarSelected" />
              </label>
              <button class="acc-btn" type="button" :disabled="!isOwner || avatarSaving" @click="removeAvatar">
                Видалити
              </button>
            </div>
            <div v-if="avatarError" class="mt-2 text-xs text-red-300">{{ avatarError }}</div>
          </div>
        </div>
      </div>

      <settings-reading-languages v-if="isOwner" />

      <settings-profile-demographics
        v-if="isOwner"
        :demographics="profile && profile.demographics ? profile.demographics : null"
        @demographics-updated="onDemographicsUpdated"
      />
    </div>
  </div>
</template>

<script lang="js">
import axios from 'axios';
import SettingsReadingLanguages from '../components/SettingsReadingLanguages.vue';
import SettingsProfileDemographics from '../components/SettingsProfileDemographics.vue';

const USERNAME_AVAILABILITY_DELAY = 350;
const USERNAME_PATTERN = /^[A-Za-z0-9_]+$/;
const USERNAME_GUIDANCE = 'Латинські літери, цифри та символ «_». До 30 символів.';
const USERNAME_MAX_REACHED_MESSAGE = 'Досягнуто максимум — 30 символів.';
const USERNAME_AVAILABILITY_MESSAGES = Object.freeze({
  invalid: USERNAME_GUIDANCE,
  checking: 'Перевіряємо доступність…',
  available: 'Юзернейм доступний',
  current: 'Це ваш поточний юзернейм',
  reclaimable: 'Цей юзернейм уже був вашим — його можна повернути',
  taken: 'Цей юзернейм уже зайнятий',
  error: 'Не вдалося перевірити доступність. Спробуйте ще раз.',
});

export default {
  name: 'SettingsProfile',
  components: {
    SettingsReadingLanguages,
    SettingsProfileDemographics,
  },
  props: {
    profile: { type: Object, default: null },
    isOwner: { type: Boolean, default: false },
  },
  data() {
    return {
      form: {
        name: '',
        username: '',
        email: '',
        bio: '',
      },
      saving: false,
      avatarSaving: false,
      avatarError: '',
      message: '',
      error: '',
      persistedUsername: '',
      usernameAvailability: 'idle',
      usernameAvailabilityTimer: null,
      usernameAvailabilityRequestId: 0,
      nameSaveError: '',
      usernameSaveError: '',
    };
  },
  computed: {
    bioLength() {
      return typeof this.form.bio === 'string' ? this.form.bio.length : 0;
    },
    showUsernameHelper() {
      return this.usernameAvailability !== 'invalid';
    },
    usernameHelperMessage() {
      return this.form.username.length === 30 && this.isUsernameLocallyValid(this.form.username)
        ? USERNAME_MAX_REACHED_MESSAGE
        : USERNAME_GUIDANCE;
    },
    usernameAvailabilityMessage() {
      return USERNAME_AVAILABILITY_MESSAGES[this.usernameAvailability] || '';
    },
    usernameAvailabilityClass() {
      if (['current', 'reclaimable'].includes(this.usernameAvailability)) {
        return 'text-[#9A4A1B] dark:text-[#F3A36F]';
      }

      if (this.usernameAvailability === 'available') {
        return 'text-emerald-700 dark:text-emerald-300';
      }

      if (['invalid', 'taken'].includes(this.usernameAvailability)) {
        return 'text-red-700 dark:text-red-300';
      }

      return 'text-white/55';
    },
  },
  watch: {
    profile: {
      immediate: true,
      handler(value) {
        if (!value) return;
        this.form.name = value.name || '';
        this.form.username = value.username || '';
        this.form.email = value.email || '';
        this.form.bio = value.bio || '';
        this.persistedUsername = value.username || '';
        this.usernameAvailability = this.form.username ? 'current' : 'empty';
        this.usernameSaveError = '';
      },
    },
  },
  methods: {
    onDemographicsUpdated(demographics) {
      this.$emit('profile-updated', { demographics });
    },
    onUsernameInput() {
      this.usernameSaveError = '';
      this.scheduleUsernameAvailabilityCheck();
    },
    clearUsernameAvailabilityTimer() {
      if (this.usernameAvailabilityTimer !== null) {
        clearTimeout(this.usernameAvailabilityTimer);
        this.usernameAvailabilityTimer = null;
      }
    },
    isUsernameLocallyValid(candidate) {
      return typeof candidate === 'string'
        && candidate.length <= 30
        && USERNAME_PATTERN.test(candidate);
    },
    scheduleUsernameAvailabilityCheck() {
      this.clearUsernameAvailabilityTimer();
      const requestId = ++this.usernameAvailabilityRequestId;
      const candidate = this.form.username;

      if (candidate === '') {
        this.usernameAvailability = 'empty';
        return;
      }

      if (!this.isUsernameLocallyValid(candidate)) {
        this.usernameAvailability = 'invalid';
        return;
      }

      this.usernameAvailability = 'checking';
      this.usernameAvailabilityTimer = setTimeout(() => {
        this.checkUsernameAvailability(candidate, requestId);
      }, USERNAME_AVAILABILITY_DELAY);
    },
    async checkUsernameAvailability(candidate, requestId) {
      try {
        const response = await axios.get('/api/account/username/availability', {
          params: { username: candidate },
        });

        if (requestId !== this.usernameAvailabilityRequestId || candidate !== this.form.username) return;

        const supportedStatuses = ['empty', 'invalid', 'current', 'available', 'reclaimable', 'taken'];
        this.usernameAvailability = supportedStatuses.includes(response.data.status)
          ? response.data.status
          : 'error';
      } catch (e) {
        if (requestId !== this.usernameAvailabilityRequestId || candidate !== this.form.username) return;
        this.usernameAvailability = 'error';
      } finally {
        if (requestId === this.usernameAvailabilityRequestId) {
          this.usernameAvailabilityTimer = null;
        }
      }
    },
    async saveProfile() {
      if (!this.isOwner) return;
      const previousUsername = this.persistedUsername;
      this.saving = true;
      this.error = '';
      this.message = '';
      this.nameSaveError = '';
      this.usernameSaveError = '';

      try {
        const response = await axios.put('/api/account/profile', {
          name: this.form.name,
          username: this.form.username,
          email: this.form.email,
          bio: this.form.bio,
        });

        this.message = response.data.message || 'Збережено.';
        const savedUsername = response.data.profile?.username || '';

        if (savedUsername !== previousUsername) {
          this.persistedUsername = savedUsername;
          window.location.reload();
          return;
        }

        this.persistedUsername = savedUsername;
        this.$emit('profile-updated', response.data.profile);
      } catch (e) {
        if (e?.response?.status === 422) {
          this.nameSaveError = e?.response?.data?.errors?.name?.[0] || '';
          this.usernameSaveError = e?.response?.data?.errors?.username?.[0] || '';
          if (this.nameSaveError || this.usernameSaveError) return;
        }
        this.error = e?.response?.data?.message || 'Помилка збереження профілю.';
      } finally {
        this.saving = false;
      }
    },
    async onAvatarSelected(event) {
      const file = event?.target?.files?.[0];
      if (!file || !this.isOwner) return;

      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
      const allowedExtensions = ['jpg', 'jpeg', 'png', 'webp'];
      const extension = file.name.split('.').pop()?.toLowerCase();

      this.avatarError = '';
      if (!allowedTypes.includes(file.type.toLowerCase()) || !allowedExtensions.includes(extension)) {
        this.avatarError = 'Підтримуються лише PNG, JPG/JPEG та WEBP.';
        event.target.value = '';
        return;
      }

      if (file.size > 2 * 1024 * 1024) {
        this.avatarError = 'Розмір зображення не може перевищувати 2 МБ.';
        event.target.value = '';
        return;
      }

      this.avatarSaving = true;
      this.error = '';
      this.message = '';

      try {
        const data = new FormData();
        data.append('avatar', file);

        const response = await axios.post('/api/account/profile/avatar', data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        this.message = response.data.message || 'Аватар оновлено.';
        this.$emit('profile-updated', { avatar: response.data.avatar });
      } catch (e) {
        this.avatarError = e?.response?.data?.errors?.avatar?.[0]
          || e?.response?.data?.message
          || 'Не вдалося завантажити аватар. Спробуйте ще раз.';
      } finally {
        this.avatarSaving = false;
        event.target.value = '';
      }
    },
    async removeAvatar() {
      if (!this.isOwner) return;

      this.avatarSaving = true;
      this.error = '';
      this.avatarError = '';
      this.message = '';

      try {
        const response = await axios.delete('/api/account/profile/avatar');
        this.message = response.data.message || 'Аватар видалено.';
        this.$emit('profile-updated', { avatar: response.data.avatar });
      } catch (e) {
        this.avatarError = e?.response?.data?.message || 'Помилка видалення аватара.';
      } finally {
        this.avatarSaving = false;
      }
    },
  },
  beforeDestroy() {
    this.clearUsernameAvailabilityTimer();
    this.usernameAvailabilityRequestId += 1;
  },
};
</script>

<style scoped>
.settings-username-input {
  padding-left: 2.5rem;
}
</style>
